/**
 * PRICE CHECK — quick backflow-price reference from any office screen.
 *
 * "What does a backflow test cost in <city>?" answered at phone-call speed.
 * One popover, three entry points (top-bar $ icon, Cmd/Ctrl+Shift+P, the
 * command palette action), plus job/site/customer pages which pre-resolve
 * the site's pinned water provider so no city typing is needed.
 *
 * Data comes from GET /api/pricing/backflow-quick-ref — the Groundwork
 * pricebook ONLY (water_providers fees + linked pricebook test items).
 * The whole list is fetched once and searched client-side as-you-type;
 * the Refresh button re-pulls.
 *
 * Office-permission gated (pricing.view): the host renders nothing for
 * techs, and the endpoint 403s them regardless.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DollarSign, Loader2, RefreshCw, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ */
/*  Open bus                                                           */
/* ------------------------------------------------------------------ */

export interface PriceCheckOpenOptions {
    /** Pre-resolved water provider (job/site/customer launch): skip the
     * search and open straight to this provider's card. */
    providerId?: string;
    providerName?: string;
}

const OPEN_EVENT = "price-check:open";

/** Open the Price Check popover from anywhere (no context plumbing needed).
 * No-op for users without pricing.view — the host isn't mounted for them. */
export function openPriceCheck(opts: PriceCheckOpenOptions = {}): void {
    window.dispatchEvent(new CustomEvent<PriceCheckOpenOptions>(OPEN_EVENT, { detail: opts }));
}

/* ------------------------------------------------------------------ */
/*  Types + helpers                                                    */
/* ------------------------------------------------------------------ */

interface QuickRefFees {
    test: number;
    city_fee: number;
    admin_fee: number;
    total: number;
}

export interface QuickRefProvider {
    id: string;
    provider: string;
    city: string;
    priced: boolean;
    domestic: QuickRefFees | null;
    fireline: QuickRefFees | null;
}

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export function filterProviders(providers: QuickRefProvider[], query: string): QuickRefProvider[] {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return providers.filter((p) => p.provider.toLowerCase().includes(q) || p.city.toLowerCase().includes(q));
}

/* ------------------------------------------------------------------ */
/*  Host — mounted once in the app shell                               */
/* ------------------------------------------------------------------ */

export function PriceCheckHost() {
    const { can } = useAuth();
    const [open, setOpen] = useState(false);
    const [pinned, setPinned] = useState<PriceCheckOpenOptions | null>(null);
    const allowed = can(PERMISSIONS.pricing.view);

    useEffect(() => {
        if (!allowed) return;
        const onOpen = (e: Event) => {
            const detail = (e as CustomEvent<PriceCheckOpenOptions>).detail ?? {};
            setPinned(detail.providerId ? detail : null);
            setOpen(true);
        };
        // Cmd/Ctrl+Shift+P — phone-call speed from the keyboard.
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "p") {
                e.preventDefault();
                setPinned(null);
                setOpen(true);
            }
        };
        window.addEventListener(OPEN_EVENT, onOpen);
        document.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener(OPEN_EVENT, onOpen);
            document.removeEventListener("keydown", onKey);
        };
    }, [allowed]);

    if (!allowed || !open) return null;
    return <PriceCheckDialog pinned={pinned} onUnpin={() => setPinned(null)} onClose={() => setOpen(false)} />;
}

/* ------------------------------------------------------------------ */
/*  Dialog                                                             */
/* ------------------------------------------------------------------ */

function PriceCheckDialog({
    pinned,
    onUnpin,
    onClose,
}: {
    pinned: PriceCheckOpenOptions | null;
    onUnpin: () => void;
    onClose: () => void;
}) {
    const [query, setQuery] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const queryClient = useQueryClient();

    const refQ = useQuery({
        queryKey: ["price-check", "quick-ref"],
        queryFn: () => api.get<{ providers: QuickRefProvider[] }>("/api/pricing/backflow-quick-ref"),
        staleTime: Infinity,
    });
    const providers = useMemo(() => refQ.data?.providers ?? [], [refQ.data]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    // Autofocus the search box the moment the popover opens (search mode).
    useEffect(() => {
        if (!pinned) inputRef.current?.focus();
    }, [pinned]);

    const refresh = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ["price-check", "quick-ref"] });
    }, [queryClient]);

    const pinnedProvider = pinned?.providerId ? (providers.find((p) => p.id === pinned.providerId) ?? null) : null;
    const matches = pinned ? [] : filterProviders(providers, query);
    const hasQuery = query.trim().length > 0;

    return (
        <div className="fixed inset-0 z-[100] animate-in fade-in duration-150" onClick={onClose} role="presentation">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
            <div
                className="fixed top-[12%] left-1/2 -translate-x-1/2 w-full max-w-xl px-4 animate-in slide-in-from-top-2 fade-in duration-200"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label="Price check"
            >
                <div className="rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl overflow-hidden">
                    {/* Header / search row */}
                    <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                        <DollarSign className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                        {pinned ? (
                            <span className="flex-1 text-sm font-semibold">Price check</span>
                        ) : (
                            <input
                                ref={inputRef}
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="City or water provider… (e.g. Springtown)"
                                aria-label="Search city or water provider"
                                className="flex-1 bg-transparent outline-none text-sm placeholder:text-foreground/60"
                            />
                        )}
                        <button
                            type="button"
                            onClick={refresh}
                            title="Refresh pricing"
                            aria-label="Refresh pricing"
                            className="rounded-md p-1.5 text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors"
                        >
                            <RefreshCw className={cn("h-4 w-4", refQ.isFetching && "animate-spin")} />
                        </button>
                        <kbd className="hidden sm:inline-flex text-[10px] text-foreground/70 bg-muted px-1.5 py-0.5 rounded font-mono leading-none">
                            ESC
                        </kbd>
                    </div>

                    {/* Body */}
                    <div className="max-h-[65vh] overflow-y-auto overscroll-contain p-3 space-y-3">
                        {refQ.isLoading && (
                            <div className="flex items-center justify-center gap-2 py-10 text-sm text-foreground/70">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading pricebook…
                            </div>
                        )}
                        {refQ.isError && !refQ.isLoading && (
                            <p className="py-8 text-center text-sm text-destructive">
                                Couldn&rsquo;t load pricing — try Refresh.
                            </p>
                        )}

                        {/* Pinned (job/site/customer launch) */}
                        {!refQ.isLoading && pinned && (
                            <>
                                <div className="flex items-center gap-1.5 px-1 text-xs text-foreground/70">
                                    <span>
                                        Provider:{" "}
                                        <span className="font-medium text-foreground">
                                            {pinnedProvider?.provider ?? pinned.providerName ?? "—"}
                                        </span>
                                    </span>
                                    <button
                                        type="button"
                                        onClick={onUnpin}
                                        className="text-primary hover:underline font-medium"
                                    >
                                        change
                                    </button>
                                </div>
                                {pinnedProvider ? (
                                    <ProviderCard provider={pinnedProvider} />
                                ) : (
                                    <p className="px-1 py-4 text-sm text-foreground/70">
                                        This provider isn&rsquo;t in the pricebook list — use{" "}
                                        <button
                                            type="button"
                                            onClick={onUnpin}
                                            className="text-primary hover:underline font-medium"
                                        >
                                            search
                                        </button>{" "}
                                        instead.
                                    </p>
                                )}
                            </>
                        )}

                        {/* Search mode */}
                        {!refQ.isLoading && !pinned && !hasQuery && (
                            <div className="py-8 text-center text-sm text-foreground/70">
                                <Search className="mx-auto mb-2 h-5 w-5" />
                                Type a city or provider to see backflow test pricing.
                            </div>
                        )}
                        {!refQ.isLoading && !pinned && hasQuery && matches.length === 0 && (
                            <p className="py-8 text-center text-sm text-foreground/70">
                                No provider matches &ldquo;{query.trim()}&rdquo;.
                            </p>
                        )}
                        {!pinned && matches.slice(0, 12).map((p) => <ProviderCard key={p.id} provider={p} />)}
                        {!pinned && matches.length > 12 && (
                            <p className="px-1 text-xs text-foreground/60">
                                {matches.length - 12} more — keep typing to narrow it down.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Provider card                                                      */
/* ------------------------------------------------------------------ */

function ProviderCard({ provider }: { provider: QuickRefProvider }) {
    return (
        <div className="rounded-lg border border-border bg-card p-3" data-testid="price-check-card">
            <div className="mb-2 flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold leading-tight">{provider.provider}</span>
                {provider.city.toLowerCase() !== provider.provider.toLowerCase() && (
                    <span className="text-xs text-foreground/70">{provider.city}</span>
                )}
            </div>
            {!provider.priced ? (
                <p className="rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-300">
                    No pricing on file — check with Regina.
                </p>
            ) : (
                // Domestic and Fireline side by side; stacks on very narrow
                // screens so nothing ever scrolls horizontally.
                <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-2">
                    <FeeColumn label="Domestic" fees={provider.domestic} />
                    <FeeColumn label="Fireline" fees={provider.fireline} />
                </div>
            )}
        </div>
    );
}

function FeeColumn({ label, fees }: { label: string; fees: QuickRefFees | null }) {
    if (!fees) {
        return (
            <div className="rounded-md border border-border/60 bg-secondary/40 p-2.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-foreground/70">{label}</p>
                <p className="mt-2 text-sm text-foreground/60">No {label.toLowerCase()} test linked.</p>
            </div>
        );
    }
    return (
        <div className="rounded-md border border-border/60 bg-secondary/40 p-2.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground/70">{label}</p>
            <dl className="mt-1.5 space-y-0.5 text-sm">
                <FeeRow label="Test" value={fees.test} />
                <FeeRow label="City fee" value={fees.city_fee} />
                <FeeRow label="Admin fee" value={fees.admin_fee} />
            </dl>
            <div className="mt-1.5 flex items-baseline justify-between border-t border-border/60 pt-1.5">
                <span className="text-xs font-semibold uppercase tracking-wider text-foreground/70">Total</span>
                <span className="text-xl font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
                    {usd.format(fees.total)}
                </span>
            </div>
        </div>
    );
}

function FeeRow({ label, value }: { label: string; value: number }) {
    return (
        <div className="flex items-baseline justify-between gap-2">
            <dt className="text-foreground/70">{label}</dt>
            <dd className="tabular-nums">{usd.format(value)}</dd>
        </div>
    );
}
